import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Database, Eye, EyeOff, Loader2 } from "lucide-react";

import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODES = [
  { key: "dry-run", label: "1. Dry run", hint: "Counts only. Writes nothing." },
  { key: "copy", label: "2. Copy", hint: "Copies every collection + indexes." },
  { key: "verify", label: "3. Verify", hint: "Re-counts both sides." },
];

// The tables that belong to the platform rather than to any one tow company.
// They currently sit inside the default tenant's own database, which is why
// that tenant can't be deleted.
// `_migration_jobs` is deliberately NOT here: it is this tool's own scratch
// log, it gains a row on every run (so a verify could never converge), and
// it is recreated in whichever database PLATFORM_DB_NAME points at.
const PLATFORM_TABLES = [
  "tenants",
  "user_directory",
  "platform_admins",
  "platform_audit",
];
const PLATFORM_DB_TARGET = "towhook_platform";

/** One-shot Atlas cutover tool. Delete this card once the move is done. */
export const ExternalMigrationCard = ({ authHeaders }) => {
  const [targetUrl, setTargetUrl] = useState("");
  const [showUrl, setShowUrl] = useState(false);
  const [targetDb, setTargetDb] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [allowNonempty, setAllowNonempty] = useState(false);
  const [resetTarget, setResetTarget] = useState(false);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(null);
  const pollRef = useRef(null);

  const running = job?.status === "running";

  useEffect(() => {
    if (!job || job.status !== "running") {
      clearInterval(pollRef.current);
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const r = await fetch(`${API}/platform/migrate/external/${job.id}`, {
          headers: authHeaders(),
        });
        if (r.ok) {
          setJob(await r.json());
        } else if (r.status === 404) {
          setJob((j) => ({
            ...j,
            status: "failed",
            error:
              "Can't find this job any more. Run Verify to see how far the copy " +
              "got, then re-run Copy with 'top it up' ticked to finish it.",
          }));
        }
      } catch {
        /* keep polling */
      }
    }, 1500);
    return () => clearInterval(pollRef.current);
  }, [job, authHeaders]);

  const start = async (mode, extra = {}) => {
    const url = extra.target_url ?? targetUrl.trim();
    const dbName = extra.target_db ?? targetDb.trim();
    if (!url || !dbName) {
      toast.error("Connection string and target database name are both required");
      return;
    }
    if (/<.*>/.test(url)) {
      toast.error("Replace <db_password> in the string with the real password");
      setShowUrl(true);
      return;
    }
    setStarting(mode);
    try {
      const r = await fetch(`${API}/platform/migrate/external`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          target_url: url,
          target_db: dbName,
          mode,
          allow_nonempty: allowNonempty,
          ...(sourceUrl.trim() ? { source_url: sourceUrl.trim() } : {}),
          ...extra,
        }),
      });
      const b = await r.json();
      if (!r.ok) {
        toast.error(b.detail || "Could not start");
        return;
      }
      setJob({ id: b.job_id, status: "running", mode, log: [] });
      toast.success(
        mode === "copy"
          ? `Copying ${b.source_db} — this can take a few minutes`
          : `${mode === "verify" ? "Verify" : "Dry run"} started — reading ${b.source_db}`
      );
    } catch {
      toast.error("Network error");
    } finally {
      setStarting(null);
    }
  };

  return (
    <div
      data-testid="external-migration-card"
      className="mt-10 rounded-lg border border-amber-500/40 bg-amber-500/5 p-5"
    >
      <div className="flex items-center gap-2">
        <Database className="h-4 w-4 text-amber-500" />
        <h3 className="text-base font-semibold">Migrate to external cluster</h3>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Copies this deployment's database to your own MongoDB Atlas cluster. Run
        the three steps in order. Nothing is written to the current database and
        the live app keeps running throughout.
      </p>

      <div className="mt-4 grid gap-3 sm:grid-cols-[2fr_1fr]">
        <div>
          <Label htmlFor="mig-url">Atlas connection string</Label>
          <div className="relative">
            <Input
              id="mig-url"
              data-testid="migration-target-url"
              type={showUrl ? "text" : "password"}
              autoComplete="off"
              spellCheck={false}
              className="pr-10 font-mono text-xs"
              placeholder="mongodb+srv://user:password@cluster.xxxxx.mongodb.net/"
              value={targetUrl}
              onChange={(e) => setTargetUrl(e.target.value)}
              disabled={running}
            />
            <button
              type="button"
              data-testid="migration-toggle-url-visibility"
              onClick={() => setShowUrl((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
              aria-label={showUrl ? "Hide connection string" : "Show connection string"}
              title={showUrl ? "Hide" : "Show so you can check the password"}
            >
              {showUrl ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Paste it, then click the eye to check you replaced{" "}
            <code>&lt;db_password&gt;</code> with the real password.
          </p>
        </div>
        <div>
          <Label htmlFor="mig-db">Target database name</Label>
          <Input
            id="mig-db"
            data-testid="migration-target-db"
            placeholder="towhook_nelson"
            value={targetDb}
            onChange={(e) => setTargetDb(e.target.value)}
            disabled={running}
          />
        </div>
      </div>

      <label className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
        <input
          type="checkbox"
          data-testid="migration-allow-nonempty"
          checked={allowNonempty}
          onChange={(e) => setAllowNonempty(e.target.checked)}
          disabled={running}
        />
        Target already has data — top it up instead of refusing
      </label>

      <button
        type="button"
        data-testid="migration-toggle-advanced"
        onClick={() => setShowAdvanced((v) => !v)}
        className="mt-3 block text-xs text-muted-foreground underline-offset-2 hover:underline"
      >
        {showAdvanced ? "Hide" : "Copy from a different cluster (after cutover)"}
      </button>
      {showAdvanced && (
        <div className="mt-2">
          <Label htmlFor="mig-source-url">
            Source connection string — leave blank to use this deployment's own database
          </Label>
          <Input
            id="mig-source-url"
            data-testid="migration-source-url"
            type="text"
            autoComplete="off"
            spellCheck={false}
            className="font-mono text-xs"
            placeholder="mongodb://... (the OLD cluster)"
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            disabled={running}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Use this once MONGO_URL already points at the new cluster, to pull
            across anything the app wrote to the old one during the switch.
          </p>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {MODES.map((m) => (
          <Button
            key={m.key}
            data-testid={`migration-${m.key}-btn`}
            variant={m.key === "copy" ? "default" : "outline"}
            size="sm"
            title={m.hint}
            disabled={running || starting !== null}
            onClick={() => start(m.key)}
          >
            {starting === m.key && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
            {m.label}
          </Button>
        ))}
      </div>

      <div
        data-testid="platform-split-section"
        className="mt-6 border-t border-amber-500/20 pt-4"
      >
        <h4 className="text-sm font-semibold">
          Move the platform tables to their own database
        </h4>
        <p className="mt-1 text-sm text-muted-foreground">
          The tenant registry, email routing, your admin login and the audit
          trail currently live inside the first tenant's database, which is why
          that company can't be deleted. This copies those four tables into{" "}
          <code className="font-mono text-xs">{PLATFORM_DB_TARGET}</code>. The
          originals are left in place — nothing is removed until you say so,
          and nothing changes until PLATFORM_DB_NAME is set.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {MODES.map((m) => (
            <Button
              key={m.key}
              data-testid={`platform-split-${m.key}-btn`}
              variant="outline"
              size="sm"
              disabled={running || starting !== null}
              onClick={() =>
                start(m.key, {
                  target_db: PLATFORM_DB_TARGET,
                  only_collections: PLATFORM_TABLES,
                  ...(m.key === "copy" ? { reset_target: resetTarget } : {}),
                })
              }
            >
              {m.label}
            </Button>
          ))}
        </div>
        <label className="mt-3 flex items-start gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            data-testid="platform-split-reset-target"
            className="mt-1"
            checked={resetTarget}
            onChange={(e) => setResetTarget(e.target.checked)}
            disabled={running || starting !== null}
          />
          <span>
            Replace the target's copies of these four tables. Tick this if the
            app has already booted against{" "}
            <code className="font-mono text-xs">{PLATFORM_DB_TARGET}</code> and
            seeded its own rows — they collide with the originals on slug and
            email, which no top-up can resolve. Leaves the source database
            untouched.
          </span>
        </label>
      </div>

      {job && (
        <div className="mt-4" data-testid="migration-progress">          <div className="flex items-center gap-2 text-sm">
            {running && <Loader2 className="h-3 w-3 animate-spin" />}
            <span className="font-medium">
              {job.mode} — {job.status}
            </span>
          </div>
          <pre
            data-testid="migration-log"
            className="mt-2 max-h-72 overflow-auto rounded bg-black/80 p-3 text-xs leading-relaxed text-green-200"
          >
            {(job.log || []).join("\n") || "starting…"}
          </pre>
          {job.status === "failed" && (
            <p data-testid="migration-error" className="mt-2 text-sm text-red-500">
              {job.error}
            </p>
          )}
          {job.status === "done" && job.result?.ok === false && (
            <p data-testid="migration-mismatch" className="mt-2 text-sm text-red-500">
              Counts do not match — do not switch MONGO_URL.
            </p>
          )}
        </div>
      )}
    </div>
  );
};
